/**
 * FormSection — UX1A Platform Primitive
 *
 * Standard section container for structured forms.
 * Groups related fields with a section title, optional description,
 * and a responsive field grid.
 *
 * Usage:
 *   <FormSection title="Personal Information" description="Basic identity fields.">
 *     <FormField label="Given name" required>
 *       <Input … />
 *     </FormField>
 *     <FormField label="Family name">
 *       <Input … />
 *     </FormField>
 *   </FormSection>
 *
 *   <FormSection title="Configuration" cols={3}>
 *     …
 *   </FormSection>
 */
import React from "react";
import { Label } from "../ui/label";

/** Section wrapper — groups related fields with a heading */
export function FormSection({ title, description, children, cols = 2, className = "" }) {
  const gridCols = {
    1: "grid-cols-1",
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
  }[cols] || "grid-cols-1 sm:grid-cols-2";

  return (
    <div className={`space-y-4 ${className}`}>
      {(title || description) && (
        <div className="border-b border-border pb-3">
          {title && (
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          )}
          {description && (
            <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
          )}
        </div>
      )}
      <div className={`grid gap-4 ${gridCols}`}>{children}</div>
    </div>
  );
}

/**
 * FormField — labeled field wrapper with inline error and required/optional marker
 *
 * Usage:
 *   <FormField label="Email" required error="Must be a valid email.">
 *     <Input type="email" … />
 *   </FormField>
 */
export function FormField({
  label,
  required = false,
  optional = false,
  error,
  hint,
  children,
  className = "",
  htmlFor,
}) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && (
        <div className="flex items-center gap-1.5">
          <Label htmlFor={htmlFor} className="text-xs font-medium">
            {label}
          </Label>
          {required && (
            <span className="text-destructive text-xs leading-none" aria-label="required">
              *
            </span>
          )}
          {optional && (
            <span className="text-muted-foreground text-[10px] leading-none">
              optional
            </span>
          )}
        </div>
      )}
      {children}
      {hint && !error && (
        <p className="text-[11px] text-muted-foreground">{hint}</p>
      )}
      {error && (
        <p className="text-[11px] text-destructive font-medium" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
